import { useQuery } from '@tanstack/react-query';
import type { DescriptionSuggestionsResponse } from '../../types/ai-description.js';
import { getHuckConfig } from '../utils/getHuckConfig.js';

export const useAIDescriptionSuggestions = (itemId: string | undefined, enabled = false) => {
  const config = getHuckConfig();

  return useQuery({
    queryKey: ['ai-description-suggestions', itemId],
    queryFn: async (): Promise<DescriptionSuggestionsResponse> => {
      if (!itemId) {
        throw new Error('Item ID is required to fetch description suggestions');
      }

      const response = await fetch(`${config.root}/api/description/${itemId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No suggestions available
          return { descriptions: [] };
        }
        throw new Error(`Failed to fetch description suggestions: ${response.statusText}`);
      }

      const data: DescriptionSuggestionsResponse = await response.json();

      // Randomize the order of suggestions on each fetch
      const shuffled = [...data.descriptions].sort(() => Math.random() - 0.5);

      return { ...data, descriptions: shuffled };
    },
    enabled: enabled && !!itemId,
    retry: false, // Don't retry on error
  });
};
